import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Anchor,
    Avatar,
    Button,
    FileButton,
    Group,
    Modal,
    Skeleton,
    Slider,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconExclamationCircle, IconUpload } from '@tabler/icons-react';
import Cropper, { Area } from 'react-easy-crop';
import { apiClient } from '../data/apiClient';
import { resolveAvatarSrc } from '../data/avatarUrl';
import { useAuth } from '../hooks/useAuth';
import { useCsrf } from '../hooks/useCsrf';

const getInitials = (name: string) => {
    const words = name.trim().split(/\s+/);
    return words.slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('');
};

const cropImageToBlob = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = 'anonymous';
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = imageSrc;
    });

    const canvas = document.createElement('canvas');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context unavailable');

    ctx.drawImage(
        img,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height,
    );

    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Canvas toBlob failed'))), 'image/jpeg', 0.92);
    });
};

const OrgSettings: React.FC = () => {
    const navigate = useNavigate();
    const { organization: homeOrg, activeOrg, isLoading, mutate } = useAuth();

    /**
     * Edit whichever org is currently active, not the user's home org.
     *
     * Watchly staff can switch into a customer, and this page must follow them
     * there — POST /organization/{id} rejects any id that is not the active org,
     * so targeting the home org while switched produced a flat 403.
     */
    const organization = activeOrg ?? homeOrg;
    const { csrfToken } = useCsrf();
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [pendingAvatar, setPendingAvatar] = useState<File | null>(null);

    const [cropSrc, setCropSrc] = useState<string | null>(null);
    const [cropFileName, setCropFileName] = useState<string>('org-avatar.jpg');
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

    const form = useForm({
        mode: 'uncontrolled',
        initialValues: { name: '' },
        validate: {
            name: value => (value.trim() ? null : 'Name is required'),
        },
    });

    useEffect(() => {
        if (organization) form.setValues({ name: organization.name });
    }, [organization]);

    useEffect(() => {
        return () => {
            if (cropSrc) URL.revokeObjectURL(cropSrc);
        };
    }, [cropSrc]);

    const handleFilePicked = (file: File | null) => {
        if (!file) return;
        setCropFileName(file.name);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setCropSrc(URL.createObjectURL(file));
    };

    const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
        setCroppedAreaPixels(areaPixels);
    }, []);

    const handleConfirmCrop = async () => {
        if (!cropSrc || !croppedAreaPixels) return;
        try {
            const blob = await cropImageToBlob(cropSrc, croppedAreaPixels);
            const file = new File([blob], cropFileName.replace(/\.[^.]+$/, '') + '.jpg', {
                type: 'image/jpeg',
            });
            setPendingAvatar(file);
            setCropSrc(null);
        } catch (err) {
            console.error('Crop failed', err);
            notifications.show({ color: 'red', title: 'Crop failed', message: 'Could not crop image.' });
        }
    };

    const handleCancelCrop = () => {
        setCropSrc(null);
    };

    const handleSubmit = async (values: typeof form.values) => {
        if (!organization) return;
        setIsSaving(true);
        setErrorMessage('');
        try {
            const formData = new FormData();
            formData.append('name', values.name);
            if (pendingAvatar) formData.append('avatar', pendingAvatar);

            await apiClient.post(`api/organization/${organization.id}`, formData, {
                headers: {
                    'X-XSRF-TOKEN': csrfToken ?? '',
                },
            });

            setPendingAvatar(null);
            await mutate();
            notifications.show({
                title: 'Organization updated',
                message: 'Your organization settings have been saved.',
            });
        } catch (err: any) {
            setErrorMessage(err?.message ?? 'Failed to save organization');
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading || !organization) {
        return (
            <Stack px="md" gap="md">
                <Skeleton height={32} width={200} />
                <Skeleton height={120} circle />
                <Skeleton height={36} />
                <Skeleton height={36} width={120} />
            </Stack>
        );
    }

    const avatarSrc = resolveAvatarSrc(organization.avatarUrl, pendingAvatar);

    return (
        <div style={{ paddingTop: 16 }}>
            <Anchor onClick={() => navigate(-1)}>Back</Anchor>
            <h2>Organization Settings</h2>
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack maw={480}>
                    <Group align="center" gap="lg">
                        <Avatar
                            src={avatarSrc}
                            size={96}
                            radius="50%"
                            style={{
                                background: avatarSrc
                                    ? undefined
                                    : 'linear-gradient(135deg, #868e96 0%, #343a40 100%)',
                            }}
                        >
                            <Text fw={700} size="lg" style={{ color: '#FFF' }}>
                                {getInitials(organization.name)}
                            </Text>
                        </Avatar>
                        <Stack gap="xs">
                            <FileButton
                                onChange={handleFilePicked}
                                accept="image/png,image/jpeg,image/webp,image/gif"
                            >
                                {props => (
                                    <Button
                                        {...props}
                                        variant="outline"
                                        leftSection={<IconUpload size="0.9rem" stroke={2} />}
                                    >
                                        {pendingAvatar ? 'Choose different image' : 'Upload image'}
                                    </Button>
                                )}
                            </FileButton>
                            {pendingAvatar && (
                                <Text size="xs" c="dimmed">
                                    {pendingAvatar.name}
                                </Text>
                            )}
                        </Stack>
                    </Group>

                    <TextInput
                        withAsterisk
                        label="Organization name"
                        placeholder="Your organization"
                        key={form.key('name')}
                        {...form.getInputProps('name')}
                    />

                    <Group justify="flex-end" mt="md">
                        <Button type="submit" loading={isSaving}>
                            Save
                        </Button>
                    </Group>

                    {errorMessage && (
                        <Alert variant="light" color="red" title="Error" icon={<IconExclamationCircle />}>
                            {errorMessage}
                        </Alert>
                    )}
                </Stack>
            </form>

            <Modal
                opened={!!cropSrc}
                onClose={handleCancelCrop}
                title="Crop your photo"
                size="lg"
                centered
            >
                {cropSrc && (
                    <Stack>
                        <div style={{ position: 'relative', width: '100%', height: 360, background: '#000' }}>
                            <Cropper
                                image={cropSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape="round"
                                showGrid={false}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        </div>
                        <Group gap="sm" align="center">
                            <Text size="sm" w={50}>Zoom</Text>
                            <Slider
                                min={1}
                                max={3}
                                step={0.01}
                                value={zoom}
                                onChange={setZoom}
                                style={{ flex: 1 }}
                            />
                        </Group>
                        <Group justify="flex-end">
                            <Button variant="default" onClick={handleCancelCrop}>
                                Cancel
                            </Button>
                            <Button onClick={handleConfirmCrop} disabled={!croppedAreaPixels}>
                                Use this crop
                            </Button>
                        </Group>
                    </Stack>
                )}
            </Modal>
        </div>
    );
};

export default OrgSettings;
